import { LoginOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import React, { type PropsWithChildren } from 'react';
import styled from 'styled-components';

import store from 'stores/RootStore';
import { AnimationPanelViews } from 'types/AnimationPanelViews';

import DrawerHeader from './DrawerHeader';

interface VisibleProps {
  $visible: boolean;
}

const Container = styled.div<VisibleProps>`
  display: ${(p) => (p.$visible ? 'flex' : 'none')};
  flex-direction: column;
  flex-shrink: 0;
  background: #f9f9f9;
  height: 100%;
  width: 430px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  border-left: 1px solid #d9d9d9;
`;

const DropIndicator = styled.div<VisibleProps>`
  position: absolute;
  top: 55px;
  flex-flow: column;
  height: calc(100% - 75px);
  width: calc(100% - 20px);
  border: 2px dashed rgba(0, 0, 0, 0.05);
  border-radius: 7px;
  margin: 10px;
  font-size: 25px;
  color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.025);

  // See the twin in Creative's AssetsDrawer for why both of these are as they
  // are: pointer-events keeps the overlay out of dragHelper's dragenter/
  // dragleave counting in either state, and visibility is what takes the prompt
  // out of the accessibility tree while still allowing the fade.
  pointer-events: none;

  transition:
    opacity 0.25s,
    visibility 0.25s;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  visibility: ${(p) => (p.$visible ? 'visible' : 'hidden')};

  .drop-icon {
    transform: rotate(90deg);
    font-size: 92px;
    opacity: 0.4;
  }
`;

const DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: auto;
`;

export type DrawerProps = PropsWithChildren<{
  label: string;
  enableDropIndicator?: boolean;
  handleOnClose?: () => void;
  headerContent?: React.ReactNode;
  panelName?: AnimationPanelViews;
  showUnreadIndicator?: boolean;
}>;

function isCurrentPanel(
  thisPanel: AnimationPanelViews | null | undefined,
  statePanel: AnimationPanelViews
) {
  if (
    !thisPanel ||
    thisPanel === AnimationPanelViews.NONE ||
    statePanel === AnimationPanelViews.NONE
  ) {
    return false;
  }
  return thisPanel === statePanel;
}

function Drawer({
  children,
  label,
  enableDropIndicator,
  handleOnClose,
  headerContent,
  panelName,
  showUnreadIndicator,
}: DrawerProps) {
  // singleton state
  const store_animation = store.animation;
  const store_ui = store.ui;

  // No animation means no panel state, so no drawer is current. Guarded here
  // rather than widening isCurrentPanel's second parameter, since this is its
  // only caller.
  const showDrawer =
    !!store_animation &&
    isCurrentPanel(panelName, store_animation.displayPanel);
  const fileDragging = store_ui.fileDragging;

  return (
    <Container $visible={showDrawer}>
      <DrawerHeader
        label={label || ''}
        handleOnClose={handleOnClose}
        showUnreadIndicator={showUnreadIndicator}
      >
        {headerContent}
      </DrawerHeader>
      <DropIndicator $visible={!!(enableDropIndicator && fileDragging)}>
        <LoginOutlined className="drop-icon" />
        <br />
        DROP FILES HERE
      </DropIndicator>
      <DrawerBody>{children}</DrawerBody>
    </Container>
  );
}

export default observer(Drawer);
